import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactHtmlParser from 'html-react-parser';
import { Link } from 'react-router-dom';
import VideoPlayer from './VideoPlayer';
import { getOptimizedThumbnail, checkAvifSupport } from "./frontendUtils.js";

const LatestPosts = ({ excludeIds }) => {
  const [articles, setArticles] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [optimizedWidth, setOptimizedWidth] = useState(window.innerWidth <= 480 ? 400 : 900);
  const [supportsAvif, setSupportsAvif] = useState(null);
  const [optimizedThumbnail, setOptimizedThumbnail] = useState({}); 

const fetchArticles = useCallback(async () => { 
  const maxRetries = 3;
  let retryCount = 0;

const fetchData = async () => {
  try {
    //Step 1: Check if articles exist in sessionStorage
    const cachedArticles = sessionStorage.getItem("latestPosts");
    const cachedThumbnails = sessionStorage.getItem("optimizedThumbnails");

    if (cachedArticles) {
      const parsedArticles = JSON.parse(cachedArticles);
      setArticles(parsedArticles);

      if (cachedThumbnails) {
        const parsedThumbnails = JSON.parse(cachedThumbnails);
        setOptimizedThumbnail(parsedThumbnails);
      }

      setLoading(false);
      setError(false);
      return; 
    }

    //Step 2: Fetch data from API if no cache exists
    const response = await axios.get("/api/latestposts", {
      params: { exclude: excludeIds.join(",") },
    });

    const data = response.data;

    //Step 3: Store articles in sessionStorage and set state
    setArticles(data);
    sessionStorage.setItem("latestPosts", JSON.stringify(data));

    //Step 4: Check AVIF support
    let supportsAvif = false;
    try {
      supportsAvif = await checkAvifSupport();
      setSupportsAvif(supportsAvif);
    } catch (avifError) {
      //console.error("[LatestPosts] Error checking AVIF support:", avifError);
    }

    //Step 5: Process & Store Optimized Thumbnails
    const optimizedMap = {};
    const optimizedIndexes = [7, 12, 21, 22, 23];

    await Promise.all(
      optimizedIndexes.map(async (index) => {
        const article = data[index];

        if (article && article.thumbnail) {
          try {
            const optimizedThumb = await getOptimizedThumbnail(article.thumbnail, optimizedWidth, supportsAvif);
            optimizedMap[index] = optimizedThumb;
          } catch (error) {
            //console.error(`[LatestPosts] Error optimizing thumbnail for Component Index ${index}:`, error);
          }
        }
      })
    );

    //Step 6: Store optimized thumbnails in sessionStorage and set state
    sessionStorage.setItem("optimizedThumbnails", JSON.stringify(optimizedMap));
    setOptimizedThumbnail(optimizedMap);

    setLoading(false);
    setError(false);
  } catch (error) {
    //console.error(`Error fetching articles (attempt ${retryCount + 1}):`, error);
    if (retryCount < maxRetries) {
      retryCount++;
      setTimeout(fetchData, 500); // Retry after 500ms
    } else {
      setError(true);
      setLoading(false);
    }
  }
};


  fetchData();
}, [excludeIds, optimizedWidth]);

useEffect(() => {
  if (excludeIds.length > 0) {
    fetchArticles();
  }
}, [fetchArticles, excludeIds]);

    return (
      <>
      <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-6" style={{ backgroundColor: 'var(--background-color)' }}>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6">
          
          <h1 
            className="border-t pt-2 font-bold md:col-span-4 lg:col-span-4"
            style={{ borderColor: 'var(--border-color)', fontFamily: 'var(--font-paragraph)', lineHeight: '1.35em' }}
          >
            More News
          </h1>

          {/* Left Column */}
          <div className="md:col-span-1 lg:col-span-1 md:border-r md:pr-4 border-b mb-4 md:border-b-0 md:mb-0">
            <Link to={articles[8]?.pageUrl}>
              <h2 className="mb-4" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.6875rem', lineHeight: '1.35em', fontWeight: '500' }}>
                {articles[8]?.title}
              </h2>
              <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1.125rem', lineHeight: '1.55em' }}>
                {ReactHtmlParser(articles[8]?.summary || '')}
              </p>
            </Link>
            <Link to={articles[9]?.pageUrl}>
              <h2 className="mb-4 mt-7 border-t pt-5 font-medium" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.25rem', lineHeight: '1.35em', fontWeight: '500' }}>
                {articles[9]?.title}
              </h2>
              <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '0.9375rem', lineHeight: '1.55em' }}>
                {ReactHtmlParser(articles[9]?.summary || '')}
              </p>
            </Link>
          </div>

          {/* Middle Column */}
          <div className="grid grid-flow-row gap-y-8 md:col-span-1 lg:col-span-2">
            <Link to={articles[7]?.pageUrl} className="block">
              <picture>
                {optimizedThumbnail[7]?.avifMobile && (
                  <source
                    srcSet={optimizedThumbnail[7].avifMobile}
                    type="image/avif"
                    media="(max-width: 480px)"
                  />
                )}
                {optimizedThumbnail[7]?.avif && (
                  <source srcSet={optimizedThumbnail[7].avif} type="image/avif" />
                )}
                {optimizedThumbnail[7]?.webp && (
                  <source srcSet={optimizedThumbnail[7].webp} type="image/webp" />
                )}
                <img
                  src={
                    optimizedThumbnail[7]?.fallback ||
                    optimizedThumbnail[7]?.webp ||
                    articles[7]?.thumbnail
                  }
                  width={optimizedWidth}
                  height={
                    articles[7]?.thumbnailWidth && articles[7]?.thumbnailHeight
                      ? (optimizedWidth * articles[7]?.thumbnailHeight) /
                        articles[7]?.thumbnailWidth
                      : 628
                  }
                  style={{
                    aspectRatio: `${articles[7]?.thumbnailWidth || 1200} / ${
                      articles[7]?.thumbnailHeight || 628
                    }`,
                  }}
                  alt={articles[7]?.imageCaption}
                  className="w-full h-auto"
                />
              </picture>

              {articles[7]?.imageCaption && (
                <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-2 text-right">{articles[7]?.imageCaption}</p>
              )}
              <h2 className="mb-4" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.4375rem', lineHeight: '1.35em', fontWeight: '500' }}>
                {articles[7]?.title}
              </h2>
              <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '0.9375rem', lineHeight: '1.55em' }}>
                {ReactHtmlParser(articles[7]?.summary || '')}
              </p>
            </Link>
          </div>

          {/* Right Column*/}
          <div className="lg:col-span-1 md:col-span-4 lg:border-l lg:pl-4 border-t pt-4 lg:border-t-0 lg:pt-0 border-b mb-4 md:border-b-0 md:mb-0">
            <Link to={articles[10]?.pageUrl}>
              <h2 className="mb-4 font-medium" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.25rem', lineHeight: '1.35em', fontWeight: '500' }}>
                {articles[10]?.title}
              </h2>
              <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1.125rem', lineHeight: '1.55em' }}>
                {ReactHtmlParser(articles[10]?.summary || '')}
              </p>
            </Link>
            <Link to={articles[11]?.pageUrl}>
              <h2 className="mb-4 mt-7 border-t pt-5 font-medium" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.25rem', lineHeight: '1.35em', fontWeight: '500' }}>
                {articles[11]?.title}
              </h2>
              <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1.125rem', lineHeight: '1.55em' }}>
                {ReactHtmlParser(articles[11]?.summary || '')}
              </p>
            </Link>
          </div>

            {/* Second Half */}
            <div className="md:border-t md:pt-4 lg:col-span-3 lg:border-t-0 lg:pt-0">
              <Link to={articles[12]?.pageUrl}>
         
                <picture>
                  {optimizedThumbnail[12]?.avifMobile && (
                    <source
                      srcSet={optimizedThumbnail[12].avifMobile}
                      type="image/avif"
                      media="(max-width: 480px)"
                    />
                  )}
                  {optimizedThumbnail[12]?.avif && (
                    <source srcSet={optimizedThumbnail[12].avif} type="image/avif" />
                  )}
                  {optimizedThumbnail[12]?.webp && (
                    <source srcSet={optimizedThumbnail[12].webp} type="image/webp" />
                  )}
                  <img
                    src={
                      optimizedThumbnail[12]?.fallback ||
                      optimizedThumbnail[12]?.webp ||
                      articles[12]?.thumbnail
                    }
                    width={optimizedWidth}
                    height={
                      articles[12]?.thumbnailWidth && articles[12]?.thumbnailHeight
                        ? (optimizedWidth * articles[12]?.thumbnailHeight) /
                          articles[12]?.thumbnailWidth
                        : 628
                    }
                    style={{
                      aspectRatio: `${articles[12]?.thumbnailWidth || 1200} / ${
                        articles[12]?.thumbnailHeight || 628
                      }`,
                    }}
                    alt={articles[12]?.imageCaption}
                    className="w-full h-auto"
                  />
                </picture>

              </Link>
              {articles[12]?.imageCaption && (
                <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-2 text-right">
                  {articles[12]?.imageCaption}
                </p>
              )}
            </div>

            <div className="md:col-span-1 md:border-t md:pt-4 lg:border-t-0 lg:pt-0 overflow-hidden">
              <Link to={articles[12]?.pageUrl}>
                <h2 className="mb-4 font-medium" style={{fontFamily:'Arial, Helvetica Neue, Helvetica, sans-serif',color:'var(--text-color)',fontSize:'1.6875rem',lineHeight:'1.35em',fontWeight:'500'}}>
                  {articles[12]?.title}
                </h2>
                <p className="mb-4" style={{fontFamily:'var(--font-paragraph)',color:'var(--text-color)',fontWeight:'400',fontSize:'1.125rem',lineHeight:'1.55em'}}>
                  {ReactHtmlParser(articles[12]?.summary || '')}
                </p>
              </Link>

              <Link to={articles[13]?.pageUrl}>
                <h2 className="mb-4 mt-7 border-t pt-5 font-medium" style={{fontFamily:'Arial, Helvetica Neue, Helvetica, sans-serif',color:'var(--text-color)',fontSize:'1.6875rem',lineHeight:'1.35em',fontWeight:'500'}}>
                  {articles[13]?.title}
                </h2>
                <p className="mb-4" style={{fontFamily:'var(--font-paragraph)',color:'var(--text-color)',fontWeight:'400',fontSize:'1.125rem',lineHeight:'1.55em'}}>
                  {ReactHtmlParser(articles[13]?.summary || '')}
                </p>
              </Link>
            </div>
              </div>
            </div>

         

          <div className="container mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-10 bg-black text-white">
            <VideoPlayer /> 
          </div>



          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-6" style={{ backgroundColor: 'var(--background-color)' }}>
        
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-6">

          
              <h1 className="border-t pt-2 font-bold md:col-span-3" style={{borderColor: 'var(--border-color)', fontFamily: 'var(--font-paragraph)', lineHeight: '1.35em'}}>More News</h1>

              {/* Left Column */}
              <div className="md:col-span-1 md:border-r md:pr-4 border-b mb-4 md:border-b-0 md:mb-0" style={{borderColor: 'var(--border-color)'}}>
                <Link to={articles[14]?.pageUrl}>
                  <h2 className="mb-4" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '27px', lineHeight: '1.35em', fontWeight: '500'}}>
                    {articles[14]?.title}
                  </h2>
                  <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '18px', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[14]?.summary || '')}
                  </p>
                </Link>

                <Link to={articles[15]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[15]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[15]?.summary || '')}
                  </p>

                </Link>
                <Link to={articles[16]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[16]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[16]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[17]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[17]?.title}</h2>
                  <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[17]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[18]?.pageUrl}>
                  <h2 className="pt-4 border-t mb-4 mt-10 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.2em', lineHeight: '1.35em', fontWeight: '500'}}>
                    {articles[18]?.title}
                  </h2>
                  <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[18]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[19]?.pageUrl}>
                    <h2 className="pt-4 border-t mb-4 mt-10 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.2em', lineHeight: '1.35em', fontWeight: '500'}}>
                      {articles[19]?.title}
                    </h2>
                    <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                      {ReactHtmlParser(articles[19]?.summary || '')}
                    </p>
                </Link>
                <Link to={articles[20]?.pageUrl}>
                    <h2 className="pt-4 border-t mb-4 mt-10 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.2em', lineHeight: '1.35em', fontWeight: '500'}}>
                      {articles[20]?.title}
                    </h2>
                    <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                      {ReactHtmlParser(articles[20]?.summary || '')}
                    </p>
                </Link>
              </div>

              {/* Middle Column (Stack Images and Content in a Vertical Layout) */}
              <div className="grid grid-flow-row gap-y-8 md:col-span-1">
                {[21, 22, 23].map((index) => (
                  <Link
                    to={articles[index]?.pageUrl}
                    className={`block ${index !== 23 ? 'border-b' : ''} ${index === 22 ? 'md:border-b-0 lg:border-b' : ''}`}
                    key={index}
                  >
                    <picture>
                      {optimizedThumbnail[index]?.avifMobile && (
                        <source
                          srcSet={optimizedThumbnail[index].avifMobile}
                          type="image/avif"
                          media="(max-width: 480px)"
                        />
                      )}
                      {optimizedThumbnail[index]?.avif && (
                        <source srcSet={optimizedThumbnail[index].avif} type="image/avif" />
                      )}
                      {optimizedThumbnail[index]?.webp && (
                        <source srcSet={optimizedThumbnail[index].webp} type="image/webp" />
                      )}
                      <img
                        src={
                          optimizedThumbnail[index]?.fallback ||
                          optimizedThumbnail[index]?.webp ||
                          articles[index]?.thumbnail
                        }
                        width={optimizedWidth || 1200}
                        height={
                          articles[index]?.thumbnailWidth && articles[index]?.thumbnailHeight
                            ? (optimizedWidth * articles[index].thumbnailHeight) / articles[index].thumbnailWidth
                            : 628
                        }
                        style={{
                          aspectRatio: `${articles[index]?.thumbnailWidth || 1200} / ${
                            articles[index]?.thumbnailHeight || 628
                          }`,
                        }}
                        alt={articles[index]?.imageCaption}
                        className="mb-2"
                      />
                    </picture>

                    {articles[index]?.imageCaption && (
                      <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-2 mr-0 text-right">
                        {articles[index].imageCaption}
                      </p>
                    )}

                    <h2
                      className="mb-4"
                      style={{
                        fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif',
                        color: 'var(--text-color)',
                        fontSize: '23px',
                        lineHeight: '1.35em',
                        fontWeight: '500',
                      }}
                    >
                      {articles[index]?.title}
                    </h2>
                  </Link>
                ))}
              </div>


              {/* Right Column (Similar to Left) */}
              <div className="lg:col-span-1 md:col-span-3 lg:border-l lg:pl-4 border-t pt-4 lg:border-t-0 lg:pt-0 border-b mb-4 md:border-b-0 md:mb-0" style={{borderColor: 'var(--border-color)'}}>
                
                <Link to={articles[24]?.pageUrl}>
                  <h2 className="mb-4 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.3em', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[24]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[24]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[25]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[25]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '18px', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[25]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[26]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[26]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '18px', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[26]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[27]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[27]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '18px', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[27]?.summary || '')}
                  </p>
                </Link>
                
                <Link to={articles[28]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[28]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '18px', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[28]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[29]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[29]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '18px', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[29]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[30]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>{articles[30]?.title}</h2>
                  <p className="lg:hidden mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '18px', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[30]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[31]?.pageUrl}>
                    <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>
                      {articles[31]?.title}
                    </h2>
                    <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                      {ReactHtmlParser(articles[31]?.summary || '')}
                    </p>
                </Link>
                <Link to={articles[32]?.pageUrl}>
                  <h2 className="pt-4 border-t mb-4 mt-10 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.2em', lineHeight: '1.35em', fontWeight: '500'}}>
                    {articles[32]?.title}
                  </h2>
                  <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[32]?.summary || '')}
                  </p>
                </Link>
                <Link to={articles[33]?.pageUrl}>
                  <h2 className="pt-5 border-t mb-4 mt-5 font-medium" style={{fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '20px', lineHeight: '1.35em', fontWeight: '500', borderColor: 'var(--border-color)'}}>
                    {articles[33]?.title}
                  </h2>
                  <p className="mb-4" style={{fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1em', lineHeight: '1.55em'}}>
                    {ReactHtmlParser(articles[33]?.summary || '')}
                  </p>
                </Link>
               
              
              </div>
            </div>
          </div>


          

        
        </>
    );
}

export default LatestPosts;
